import { Button, Flex, Tabs, Text } from '@mantine/core';
import pluralize from 'pluralize';
import { useState } from 'react';
import { FileUploadInput, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useFlags } from 'src/feature-flags/FlagsHook';
import SelectableItemList from 'src/UI/drawer-flow/SelectableItemList';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import { MeetingNoteStepProps, MeetingNoteSourceType } from '../types';
import styles from './NoteSourceStep.module.css';

const AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'audio/mp4',
  'audio/x-m4a',
  'video/mp4',
  'audio/aac',
  'audio/flac',
  'audio/ogg',
  'audio/webm',
];

const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
];

const DOCUMENT_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.md'];

type SourceTab = 'recordings' | 'documents';

export function NoteSourceStep({ data, setData }: MeetingNoteStepProps) {
  const { flagEnabledMeetingNoteFromDocument } = useFlags();
  const { files, isLoading } = useFileUploads({ clientId: data.client?.id });
  const { uploadFiles } = useFileUploadActions({ clientId: data.client?.id });
  const [isUploading, setIsUploading] = useState(false);

  const audioItems = filterAudioFiles(files).map(file => ({
    id: file.id,
    title: file.file.fileName,
    description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
  }));

  const documentItems = filterDocumentFiles(files).map(file => ({
    id: file.id,
    title: file.file.fileName,
    description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
  }));

  const defaultTab: SourceTab =
    audioItems.length === 0 && documentItems.length > 0 ? 'documents' : 'recordings';
  const [activeTab, setActiveTab] = useState<SourceTab>(defaultTab);

  const handleTabChange = (value: string | null) => {
    const tab = (value ?? 'recordings') as SourceTab;
    setActiveTab(tab);
    setData(prev => ({
      ...prev,
      selectedRecordingId: undefined,
      selectedSourceType: undefined,
      selectedSourceName: undefined,
    }));
  };

  const allItems = [...audioItems, ...documentItems];

  const handleSelection = (selectedIds: string[], sourceType: MeetingNoteSourceType) => {
    const selectedId = selectedIds[0] || undefined;
    const selectedItem = allItems.find(i => i.id === selectedId);
    setData(prev => ({
      ...prev,
      selectedRecordingId: selectedId,
      selectedSourceType: selectedId ? sourceType : undefined,
      selectedSourceName: selectedItem?.title,
    }));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList) {
      setIsUploading(true);
      try {
        const payload: FileUploadInput[] = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
        }));
        const result = await uploadFiles(payload);
        const newUploadedFileId = result[0]?.data?.id;
        if (newUploadedFileId) {
          const uploadedFileName = Array.from(fileList)[0]?.name;
          setData(prev => ({
            ...prev,
            selectedRecordingId: newUploadedFileId,
            selectedSourceType: 'document',
            selectedSourceName: uploadedFileName,
          }));
        }
      } catch (error) {
        notifyAndLogError(
          'Failed to upload file',
          'An error occurred while uploading the document. Please try again.',
          error,
        );
      }
      setIsUploading(false);
    }

    event.target.value = '';
  };

  if (isLoading) return null;

  if (!flagEnabledMeetingNoteFromDocument) {
    return (
      <Flex direction="column" h="100%" mih={0}>
        <StepCard title="Select recording">
          <Flex direction="column" gap="xs" mih={0} flex={1} h="100%">
            <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black" style={{ flexShrink: 0 }}>
              {audioItems.length} {pluralize('recording', audioItems.length)} found
            </Text>
            <SelectableItemList
              items={audioItems}
              hint="The information already uploaded to the client's profile, financial and cashflows areas will be added as supplementary information in the meeting note."
              selectedItemIds={data.selectedRecordingId ? [data.selectedRecordingId] : []}
              setSelectedItemIds={selectedIds => handleSelection(selectedIds, 'audio')}
              selectMultiple={false}
              emptyView={
                <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                  Upload a meeting recording (MP3, MP4, M4A, or WAV) to the client&apos;s documents
                  and it will appear here.
                </Text>
              }
            />
          </Flex>
        </StepCard>
      </Flex>
    );
  }

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard title="Select source">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          flex={1}
          mih={0}
          display="flex"
          style={{ flexDirection: 'column' }}
          classNames={{ list: styles.tabList, tab: styles.tab }}
        >
          <Tabs.List style={{ flexShrink: 0 }}>
            <Tabs.Tab value="recordings">Recordings ({audioItems.length})</Tabs.Tab>
            <Tabs.Tab value="documents">Documents ({documentItems.length})</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="recordings" pt="sm" flex={1} mih={0}>
            <Flex direction="column" gap="xs" mih={0} flex={1} h="100%">
              <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black" style={{ flexShrink: 0 }}>
                {audioItems.length} {pluralize('recording', audioItems.length)} found
              </Text>
              <SelectableItemList
                items={audioItems}
                hint="The information already uploaded to the client's profile, financial and cashflows areas will be added as supplementary information in the meeting note."
                selectedItemIds={
                  data.selectedSourceType === 'audio' && data.selectedRecordingId
                    ? [data.selectedRecordingId]
                    : []
                }
                setSelectedItemIds={selectedIds => handleSelection(selectedIds, 'audio')}
                selectMultiple={false}
                emptyView={
                  <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                    Upload a meeting recording (MP3, MP4, M4A, or WAV) to the client&apos;s
                    documents and it will appear here.
                  </Text>
                }
              />
            </Flex>
          </Tabs.Panel>

          <Tabs.Panel value="documents" pt="sm" flex={1} mih={0}>
            <Flex direction="column" gap="xs" mih={0} flex={1} h="100%">
              <Flex justify="space-between" align="center" style={{ flexShrink: 0 }}>
                <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black">
                  {documentItems.length} {pluralize('document', documentItems.length)} found
                </Text>
                <Button
                  component="label"
                  variant="subtle"
                  size="compact-sm"
                  loading={isUploading}
                  style={{ cursor: 'pointer' }}
                >
                  Upload document
                  <input
                    type="file"
                    accept={DOCUMENT_FILE_EXTENSIONS.join(',')}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />
                </Button>
              </Flex>
              <SelectableItemList
                items={documentItems}
                hint="The document content will be used as the transcript for meeting note generation."
                selectedItemIds={
                  data.selectedSourceType === 'document' && data.selectedRecordingId
                    ? [data.selectedRecordingId]
                    : []
                }
                setSelectedItemIds={selectedIds => handleSelection(selectedIds, 'document')}
                selectMultiple={false}
                emptyView={
                  <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                    Upload a transcript document (PDF, Word, or text file) using the button above
                    and it will appear here.
                  </Text>
                }
              />
            </Flex>
          </Tabs.Panel>
        </Tabs>
      </StepCard>
    </Flex>
  );
}

function filterAudioFiles(files: UploadedFile[]): UploadedFile[] {
  return files.filter(file => AUDIO_MIME_TYPES.includes(file.file.fileType));
}

function filterDocumentFiles(files: UploadedFile[]): UploadedFile[] {
  return files.filter(file => DOCUMENT_MIME_TYPES.includes(file.file.fileType));
}
